// src/pages/portal/PortalAddNames.js — the partner-facing side of the "Add
// Names" workflow (migrations/041_contact_submissions.sql,
// src/controllers/portalController.js: submitContactNames/listMySubmissions).
// A partner submits raw names (firm, name, email and notes required) for a mandate
// they're engaged on. work_type is no longer asked for; the server stamps
// each submission with the mandate's own work_type. Each name has a type
// (investor, strategic advisory, business development / client
// acquisition; migrations/071, 089). All three are offered on every mandate
// (Travis, 10/29), preselected from the mandate's work type in its signed
// EL; the choice above the grid sets every line, and More changes one line.
// They land as 'pending' and show up on the partner's own Contacts page
// (Pending Approval table) until RGA reviews them — a review/approval step
// that isn't built yet, see the plan doc referenced in portalController.js.
import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import MandateHeader from '../../components/portal/MandateHeader'
import { mandateOverviewTitle, PORTAL_MANDATES_TITLE } from '../../lib/portalPageTitles'
import { COUNTRY_CODES, DEFAULT_COUNTRY_ISO } from '../../lib/countryCodes'
import { PORTAL_NAME_TYPES, defaultNameType, BUSINESS_DEVELOPMENT, CFC_FIELD_DEFS } from '../../lib/nameTypes'
import { parseNameList } from '../../lib/parseNameList'
import SuggestInput from '../../components/portal/SuggestInput'

let nextRowId = 1
function blankRow(fields = {}) {
  return {
    id: nextRowId++,
    contact_name: '', firm_name: '', title_role: '', email: '',
    phone: '', phone_country: DEFAULT_COUNTRY_ISO,
    partner_notes: '',
    name_type: null,   // null = the mandate's default, until the partner picks
    // Business Development / Client Acquisition only (migrations/074)
    ...Object.fromEntries(CFC_FIELD_DEFS.map(f => [f.key, ''])),
    ...fields,
  }
}

// Combines the picked country's dial code with the number the partner
// typed into a single string — contact_submissions.phone is just TEXT, so
// the country picker is UI-only state, not its own column.
function combinedPhone(phone, iso) {
  const num = phone.trim()
  if (!num) return ''
  const country = COUNTRY_CODES.find(c => c.iso === iso)
  return country ? `${country.dial} ${num}` : num
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171', marginBottom: 16,
    }}>{msg}</div>
  )
}

// A native <select> can't show one thing closed and another open, so this
// is a small custom dropdown instead: the closed button stays compact
// (dial code + ISO, e.g. "+1 US") to preserve space in the row, while the
// open list spells out full country names — United States first, then the
// rest alphabetically (COUNTRY_CODES' own order).
function PhoneCountryPicker({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const selected = COUNTRY_CODES.find(c => c.iso === value) || COUNTRY_CODES[0]

  useEffect(() => {
    if (!open) return
    function onClickAway(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickAway)
    return () => document.removeEventListener('mousedown', onClickAway)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" className="select" style={{ width: 84, textAlign: 'left' }}
        onClick={() => setOpen(o => !o)}>
        {selected.dial} {selected.iso}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
          width: 240, maxHeight: 260, overflowY: 'auto',
          background: 'var(--surface-raised)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}>
          {COUNTRY_CODES.map(c => (
            <div
              key={c.iso}
              onClick={() => { onChange(c.iso); setOpen(false) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                color: c.iso === value ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: c.iso === value ? 'var(--surface-hover)' : 'transparent',
              }}
            >
              {c.name} ({c.dial})
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Partner's own directory, for the typeahead ───────────────────────────
// Only what this partner can already see (Travis, 10/27): the investors
// attributed to them (my-investors) and their own submissions that RGA did
// not turn down (my-submissions). Both endpoints run under the partner's own
// RLS, so nothing from the wider CRM can appear here.
function useDirectory(mandateId) {
  const inv = useQuery({
    queryKey: ['portalMyInvestors'],
    queryFn: () => api.get('/api/portal/my-investors').then(r => r.data.data ?? []),
  })
  const subs = useQuery({
    queryKey: ['portalMySubmissions'],
    queryFn: () => api.get('/api/portal/my-submissions').then(r => r.data.data ?? []),
  })
  const people = new Map()
  const firms = new Map()
  const key = (n, f) => `${(n || '').trim().toLowerCase()}|${(f || '').trim().toLowerCase()}`
  const addFirm = f => { const t = (f || '').trim(); if (t && !firms.has(t.toLowerCase())) firms.set(t.toLowerCase(), t) }
  for (const r of inv.data ?? []) {
    addFirm(r.investor_firm_name)
    if (!r.investor_contact_name) continue
    const k = key(r.investor_contact_name, r.investor_firm_name)
    const p = people.get(k) ?? { name: r.investor_contact_name, firm: r.investor_firm_name ?? '', title: '', email: '', phone: '', here: null }
    p.title ||= r.title_role ?? ''
    p.email ||= (Array.isArray(r.email) ? r.email[0] : r.email) ?? ''
    p.phone ||= (Array.isArray(r.phone) ? r.phone[0] : r.phone) ?? ''
    if (r.mandate_id === mandateId) p.here = `Already on this mandate · ${r.stage}`
    people.set(k, p)
  }
  for (const s of subs.data ?? []) {
    if (s.status === 'rejected') continue
    addFirm(s.firm_name)
    const k = key(s.contact_name, s.firm_name)
    const p = people.get(k) ?? { name: s.contact_name, firm: s.firm_name ?? '', title: '', email: '', phone: '', here: null }
    p.title ||= s.title_role ?? ''
    p.email ||= s.email ?? ''
    p.phone ||= s.phone ?? ''
    if (s.mandate_id === mandateId && !p.here) p.here = s.status === 'pending' ? 'Already submitted for this mandate' : 'Already on this mandate'
    people.set(k, p)
  }
  return { people: [...people.values()], firms: [...firms.values()] }
}

// Every word typed starts a word in the text ("acm cap" finds "Acme Capital").
function matches(text, q) {
  const words = (text || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
  return q.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).every(w => words.some(x => x.startsWith(w)))
}

const isFilled = r => [r.firm_name, r.contact_name, r.partner_notes, r.title_role, r.email, r.phone].some(v => v.trim())
const validEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())
const REQUIRED = ['firm_name', 'contact_name', 'email', 'partner_notes']
const missing = r => isFilled(r) && (REQUIRED.some(f => !r[f].trim()) || !validEmail(r.email))

const COLS = '26px minmax(150px, 1.15fr) minmax(150px, 1.15fr) minmax(110px, 0.85fr) minmax(150px, 1fr) minmax(220px, 2fr) 92px'
const cellInput = { padding: '7px 9px', fontSize: 13 }
const req = <span style={{ color: 'var(--accent)' }}>*</span>

function NameRow({ row, index, total, onChange, onRemove, onEnter, onPasteList, defaultType, directory, attempted }) {
  const [more, setMore] = useState(false)
  function set(field, value) { onChange(row.id, { ...row, [field]: value }) }
  const type = row.name_type ?? defaultType
  const cfc = type === BUSINESS_DEVELOPMENT
  const bad = attempted && missing(row)
  const border = field => bad && (!row[field].trim() || (field === 'email' && !validEmail(row.email))) ? { borderColor: 'var(--danger, #ef4444)' } : null
  // Safari ignores autocomplete="off" and offers the viewer's own contact
  // card from field names and placeholders; a "search" name and a zero-width
  // joiner in the placeholder words keep it out of the grid.
  const cell = field => ({
    className: 'input', 'data-cell': `${row.id}-${field}`, name: `search-${field}-${row.id}`,
    autoComplete: 'off', 'data-1p-ignore': true, 'data-lpignore': 'true',
    style: { ...cellInput, ...border(field) },
  })
  const known = row.contact_name.trim() && directory.people.find(p =>
    p.name.toLowerCase() === row.contact_name.trim().toLowerCase() &&
    (!row.firm_name.trim() || p.firm.toLowerCase() === row.firm_name.trim().toLowerCase()))

  // Pasting several lines (or tab-separated cells) fills rows from here.
  function handlePaste(e) {
    const text = e.clipboardData.getData('text')
    if (!/[\n\t]/.test(text.trim())) return
    e.preventDefault()
    onPasteList(row.id, text)
  }

  const pickPerson = p => onChange(row.id, {
    ...row,
    contact_name: p.name,
    firm_name: row.firm_name.trim() ? row.firm_name : p.firm,
    title_role: row.title_role.trim() ? row.title_role : p.title,
    email: row.email.trim() ? row.email : p.email,
    phone: row.phone.trim() ? row.phone : p.phone,
  })

  return (
    <div style={{ borderBottom: '1px solid var(--border)', padding: '6px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{index + 1}</span>
        <SuggestInput
          value={row.firm_name}
          onChange={v => set('firm_name', v)}
          suggest={q => directory.firms.filter(f => matches(f, q)).map(f => ({ key: f, label: f }))}
          onPick={it => set('firm_name', it.label)}
          onEnter={() => onEnter(row.id)}
          onPaste={handlePaste}
          inputProps={{ ...cell('firm_name'), placeholder: cfc ? 'Com\u200Cpany' : 'Fi\u200Crm' }}
        />
        <SuggestInput
          value={row.contact_name}
          onChange={v => set('contact_name', v)}
          suggest={q => directory.people
            .filter(p => matches(p.name, q))
            .sort((a, b) => (b.firm.toLowerCase() === row.firm_name.trim().toLowerCase()) - (a.firm.toLowerCase() === row.firm_name.trim().toLowerCase()))
            .map(p => ({ key: `${p.name}|${p.firm}`, label: p.name, sub: [p.title, p.firm].filter(Boolean).join(' · ') + (p.here ? ` · ${p.here}` : ''), person: p }))}
          onPick={it => pickPerson(it.person)}
          onEnter={() => onEnter(row.id)}
          onPaste={handlePaste}
          inputProps={{ ...cell('contact_name'), placeholder: 'Full na\u200Cme' }}
        />
        <input {...cell('title_role')} value={row.title_role} placeholder={'Ti\u200Ctle'}
          onChange={e => set('title_role', e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(row.id) } }} />
        <input {...cell('email')} inputMode="email" value={row.email} placeholder={'E\u200Cmail'}
          onChange={e => set('email', e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(row.id) } }} />
        <input {...cell('partner_notes')} value={row.partner_notes}
          placeholder="Why they fit, and how you know them"
          onChange={e => set('partner_notes', e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(row.id) } }} />
        <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMore(m => !m)}
            title="Phone and type of name" style={{ padding: '6px 8px', gap: 4, display: 'inline-flex', alignItems: 'center' }}>
            {more ? 'Less' : 'More'}
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"
              style={{ transform: more ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
              <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {total > 1 && (
            <button type="button" className="btn-icon" onClick={() => onRemove(row.id)}
              title="Remove this name" aria-label="Remove this name">
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {(known?.here || bad) && (
        <div style={{ marginLeft: 34, marginTop: 4, fontSize: 11.5, display: 'flex', gap: 14 }}>
          {known?.here && <span style={{ color: 'var(--text-secondary)' }}>{known.here}</span>}
          {bad && <span style={{ color: 'var(--danger-text, #f87171)' }}>{row.email.trim() && !validEmail(row.email) ? 'Check the email address' : 'Firm, name, email and notes are needed for this name'}</span>}
        </div>
      )}

      {more && (
        <div style={{ marginLeft: 34, marginTop: 10, marginBottom: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Type of name</label>
            <select className="select" value={type} onChange={e => set('name_type', e.target.value)}>
              {PORTAL_NAME_TYPES.map(t => <option key={t.value} value={t.value}>{t.partnerLabel ?? t.label}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Phone</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <PhoneCountryPicker value={row.phone_country} onChange={v => set('phone_country', v)} />
              <input className="input" name={`search-phone-${row.id}`} autoComplete="off" inputMode="tel"
                value={row.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 555-5555" />
            </div>
          </div>
          {cfc && CFC_FIELD_DEFS.map(f => (
            <div key={f.key} className="input-group">
              <label className="input-label">{f.label}</label>
              {f.kind === 'select' ? (
                <select className="select" value={row[f.key]} onChange={e => set(f.key, e.target.value)}>
                  <option value="">Choose</option>
                  {f.options.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="input" value={row[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// The + beside Submit that adds the next line, lined up with the left
// edge of the Firm column (row number 26px + gap 8px).
function AddLineButton({ onClick }) {
  const [hover, setHover] = useState(false)
  return (
    <div style={{ paddingLeft: 34 }}>
      <button type="button" onClick={onClick} title="Add another name" aria-label="Add another name"
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        style={{
          width: 34, height: 34, borderRadius: '50%', padding: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: '1.5px solid var(--success)', color: hover ? '#fff' : 'var(--success)',
          background: hover ? 'var(--success)' : 'transparent', transition: 'background 0.15s, color 0.15s',
        }}>
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}

export default function PortalAddNames() {
  const { id } = useParams()
  const qc = useQueryClient()
  const [rows, setRows] = useState(() => [blankRow()])
  const [err, setErr] = useState(null)
  const [justSubmitted, setJustSubmitted] = useState(0)
  const [attempted, setAttempted] = useState(false)
  const [focusCell, setFocusCell] = useState(null)
  const directory = useDirectory(id)

  // Move the cursor once the row it points at has rendered.
  useEffect(() => {
    if (!focusCell) return
    document.querySelector(`[data-cell="${focusCell}"]`)?.focus()
    setFocusCell(null)
  }, [focusCell, rows])

  const { data: mandate, isLoading, error: loadError } = useQuery({
    queryKey: ['portalMandateDetail', id],
    queryFn: () => api.get(`/api/portal/mandates/${id}`).then(r => r.data.data),
  })

  const submitMutation = useMutation({
    mutationFn: (names) => api.post(`/api/portal/mandates/${id}/submissions`, { names }),
    onSuccess: (_, names) => {
      qc.invalidateQueries({ queryKey: ['portalMySubmissions', id] })
      qc.invalidateQueries({ queryKey: ['portalMySubmissions'] })
      setRows([blankRow()])
      setErr(null)
      setAttempted(false)
      setJustSubmitted(names.length)
      setTimeout(() => setJustSubmitted(0), 6000)
    },
    onError: (e) => setErr(e.response?.data?.error || e.message),
  })

  // The type for every line not changed under More: the mandate's default
  // until the partner picks another above the grid.
  const [pageType, setPageType] = useState(null)
  // The portal has no Strategic advisory type; those mandates start on
  // Business Development instead.
  const elType = defaultNameType(mandate?.work_type, mandate?.work_subtype)
  const defaultType = pageType ?? (elType === 'Strategic advisory' ? BUSINESS_DEVELOPMENT : elType)

  // One line to start; the + under the grid (or Enter on the last line)
  // adds the next. There is always at least one line.
  const atLeastOne = rs => rs.length ? rs : [blankRow()]
  function updateRow(rowId, next) {
    setRows(rs => rs.map(r => r.id === rowId ? next : r))
  }
  function removeRow(rowId) { setRows(rs => atLeastOne(rs.filter(r => r.id !== rowId))) }

  // Enter moves to the next row's firm, adding a row at the end.
  function nextRow(rowId) {
    const i = rows.findIndex(r => r.id === rowId)
    if (i < rows.length - 1) { setFocusCell(`${rows[i + 1].id}-firm_name`); return }
    const fresh = blankRow()
    setRows(rs => [...rs, fresh])
    setFocusCell(`${fresh.id}-firm_name`)
  }

  // Parsed names replace the empty rows from this one on, in order.
  function insertList(parsed, atRowId = null) {
    if (!parsed.length) return
    setRows(rs => {
      const at = atRowId == null ? rs.findIndex(r => !isFilled(r)) : rs.findIndex(r => r.id === atRowId)
      const start = at < 0 ? rs.length : at
      const before = rs.slice(0, start)
      const after = rs.slice(start).filter(isFilled)
      const startHere = atRowId != null && rs[start] && isFilled(rs[start]) ? [rs[start]] : []
      return atLeastOne([...before, ...startHere, ...parsed.map(p => blankRow(p)), ...after.filter(r => !startHere.includes(r))])
    })
  }

  const filled = rows.filter(isFilled)
  const incomplete = filled.filter(missing).length

  function handleSubmit(e) {
    e.preventDefault()
    // Empty rows are ignored; a row with anything in it needs firm, name,
    // email and notes (marked * in the header).
    setAttempted(true)
    if (!filled.length) { setErr('Add at least one name'); return }
    if (incomplete) {
      setErr(`${incomplete} name${incomplete === 1 ? ' is' : 's are'} missing a firm, name, email or notes (highlighted below)`); return
    }
    const names = filled.map(({ id: _id, phone, phone_country, name_type, ...rest }) => ({
      ...rest,
      phone: combinedPhone(phone, phone_country),
      name_type: name_type ?? defaultType,
    }))
    submitMutation.mutate(names)
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (loadError || !mandate) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Mandate not found</h3>
          <p><Link to="/portal/mandates">Back to {PORTAL_MANDATES_TITLE}</Link></p>
        </div>
      </div>
    )
  }

  if (!mandate.engaged) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Not available</h3>
          <p>You can only submit names on mandates you're engaged on.</p>
          <p><Link to={`/portal/mandates/${id}`}>Back to {mandateOverviewTitle(mandate)}</Link></p>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <MandateHeader
        mandate={mandate}
        back={{ to: `/portal/mandates/${id}`, label: mandateOverviewTitle(mandate) }}
        showSector={false}
        title="Add Names"
        description="Submit names for RGA to review. Approved names move into your pipeline."
      />

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <form onSubmit={handleSubmit}>
          <ErrorBanner msg={err} />

          {justSubmitted > 0 && (
            <div style={{
              background: 'var(--success-muted)', border: '1px solid rgba(52,211,153,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#34d399', marginBottom: 16,
            }}>{justSubmitted} name{justSubmitted === 1 ? '' : 's'} submitted. RGA will review and follow up.</div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap', marginBottom: 14, paddingLeft: 34 }}
            role="group" aria-label="Type of name" title="Sets every line. Change an individual name under More.">
            {PORTAL_NAME_TYPES.map(t => (
              <button type="button" key={t.value} onClick={() => setPageType(t.value)} aria-pressed={defaultType === t.value}
                className={`btn btn-sm ${defaultType === t.value ? 'btn-primary' : 'btn-ghost'}`}>{t.partnerLabel ?? t.label}</button>
            ))}
          </div>
          {defaultType === BUSINESS_DEVELOPMENT && mandate.bd_targets && (
            <div style={{ paddingLeft: 34, marginTop: -6, marginBottom: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
              Looking for: <span style={{ color: 'var(--text-primary)' }}>{mandate.bd_targets}</span>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 940 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: COLS, gap: 8, padding: '0 0 6px',
                borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)',
              }}>
                <span />
                <span>{defaultType === BUSINESS_DEVELOPMENT ? 'Company' : 'Firm'} {req}</span>
                <span>Name {req}</span>
                <span>Title</span>
                <span>Email {req}</span>
                <span>Notes {req}</span>
                <span />
              </div>
              {rows.map((row, i) => (
                <NameRow key={row.id} row={row} index={i} total={rows.length}
                  onChange={updateRow} onRemove={removeRow} onEnter={nextRow}
                  onPasteList={(rowId, text) => insertList(parseNameList(text), rowId)}
                  defaultType={defaultType} directory={directory} attempted={attempted} />
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, gap: 12 }}>
            <AddLineButton onClick={() => nextRow(rows[rows.length - 1].id)} />
            <button type="submit" className="btn btn-primary" disabled={submitMutation.isPending || !filled.length}>
              {submitMutation.isPending ? 'Submitting…'
                : filled.length ? `Submit ${filled.length} name${filled.length === 1 ? '' : 's'} for review` : 'Submit for review'}
            </button>
          </div>
        </form>
      </div>

    </div>
  )
}
